// src/components/Ouvidoria/PublicOuvidoriaForm.js

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FaPaperPlane, FaCheckCircle, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import PolicyModal from './PolicyModal'; // Importa o novo componente de política

// IMPORTANTE: Cole aqui a URL da sua Cloud Function "enviarDenunciaPublica"
const CLOUD_FUNCTION_URL = 'https://enviardenunciapublica-aap3px27dq-rj.a.run.app'; 

const PublicOuvidoriaForm = () => {
    // Estados do formulário principal
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('denuncia');
    const [empresaId, setEmpresaId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [protocol, setProtocol] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estados para a identificação opcional
    const [wantsToIdentify, setWantsToIdentify] = useState(false);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');

    // Estado para controlar a visibilidade do modal da política
    const [isPolicyVisible, setIsPolicyVisible] = useState(false);

    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('empresaId');
        if (id) {
            setEmpresaId(id);
        } else {
            setError('Link de denúncia inválido ou incompleto.');
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!empresaId) {
            setError('A identificação da empresa não foi encontrada no link.');
            return;
        }
        if (message.trim().length < 10) {
            setError('A mensagem deve ter pelo menos 10 caracteres.');
            return;
        }
        if (wantsToIdentify && (!userName.trim() || !userEmail.trim())) {
            setError('Nome e E-mail são obrigatórios para se identificar.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        const submissionData = {
            empresaId,
            message,
            category,
            isAnonymous: !wantsToIdentify,
            userName: wantsToIdentify ? userName : 'Anônimo (Público)',
            userEmail: wantsToIdentify ? userEmail : null,
        };

        try {
            const response = await fetch(CLOUD_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = typeof data === 'string' ? data : 'Erro ao enviar a denúncia.';
                throw new Error(errorMessage);
            }
            
            setProtocol(data.protocol);
            setSuccess(true);

        } catch (err) {
            console.error("Erro ao chamar a Cloud Function:", err);
            setError(err.message || 'Não foi possível enviar sua mensagem. Verifique sua conexão e tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (error) {
        return <div className="error-message"><FaExclamationTriangle/> {error}</div>;
    }

    if (success) {
        return (
            <div className="ouvidoria-form-card success-message">
                <FaCheckCircle size={50} className="success-icon" />
                <h3>Manifestação Enviada!</h3>
                <p>Sua mensagem foi recebida com sucesso e de forma confidencial.</p>
                <div className="protocol-box">
                    <strong>Guarde seu número de protocolo para consultar a resposta:</strong>
                    <span>{protocol}</span>
                    <small>Este é o único meio de acompanhar sua manifestação.</small>
                </div>
            </div>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit} className="public-form">
                <div className="form-group">
                    <label htmlFor="category">Qual o tipo da sua mensagem?</label>
                    <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} className="form-control">
                        <option value="denuncia">Denúncia</option>
                        <option value="sugestao">Sugestão ou Ideia</option>
                        <option value="reclamacao">Reclamação ou Problema</option>
                        <option value="elogio">Elogio</option>
                        <option value="outro">Outro Assunto</option>
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="message">Sua mensagem:</label>
                    <textarea
                        id="message"
                        rows="8"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Escreva sua mensagem detalhadamente aqui..."
                        className="form-control"
                        required
                    />
                </div>

                <div className="form-group-toggle">
                  <label htmlFor="identify-toggle" className="toggle-label">
                    <input
                      id="identify-toggle"
                      type="checkbox"
                      checked={wantsToIdentify}
                      onChange={(e) => setWantsToIdentify(e.target.checked)}
                      className="toggle-checkbox"
                    />
                    <span className="toggle-switch"></span>
                    <span className="toggle-text">Desejo me identificar (Opcional)</span>
                  </label>
                </div>

                {wantsToIdentify && (
                    <div className="identity-fields">
                        <div className="anonymous-info" style={{ marginTop: 0 }}>
                            <p><FaPaperPlane /> Seus dados serão visíveis para a equipe de gestão responsável.</p>
                        </div>
                        <div className="form-group">
                            <label htmlFor="userName">Seu Nome Completo</label>
                            <input type="text" id="userName" value={userName} onChange={e => setUserName(e.target.value)} className="form-control" required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="userEmail">Seu E-mail de Contato</label>
                            <input type="email" id="userEmail" value={userEmail} onChange={e => setUserEmail(e.target.value)} className="form-control" required />
                        </div>
                    </div>
                )}
                
                <button type="submit" className="btn-primary btn-submit-ouvidoria" disabled={isSubmitting || !empresaId}>
                    {isSubmitting ? 'Enviando...' : 'Enviar Manifestação'}
                </button>
            </form>

            <div className="policy-link-container">
                <FaLock /> Ao enviar, você concorda com nossa <button type="button" onClick={() => setIsPolicyVisible(true)} className="link-button">Política de Uso e Confidencialidade</button>.
            </div>

            <PolicyModal isOpen={isPolicyVisible} onClose={() => setIsPolicyVisible(false)} />
        </>
    );
};

export default PublicOuvidoriaForm;